import { useMemo } from 'react'
import { constructDynamicFilters } from '@/hooks/useDocumentGrid'
import { useGetDocumentsCount } from '@/service-library/hooks/documents'
import { useDocumentFiltersContext } from '@/components/document-filters/DocumentFiltersProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

export default function useDocumentCountPerWorkflowState() {
  const { isApplyingFilters, tableCuratedFilters } = useDocumentFiltersContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { noFlags, flagCodes, filtering, orgIds, teamIds, tagIds } =
    tableCuratedFilters

  const { groupedCounts } = useGetDocumentsCount({
    filters: {
      '~group-by':
        'workflow_states__id,document_workflow_states__processing_started_at:isnull',
      '~group-by-use-distinct': 'True',
      workflow_states__workflow_id: selectedWorkflow?.id,
    },
    refetchInterval: 2000,
    enabled: !!selectedWorkflow?.id,
  })

  const { groupedCounts: filteredGroupedCounts } = useGetDocumentsCount({
    filters: {
      '~group-by':
        'workflow_states__id,document_workflow_states__processing_started_at:isnull',
      '~group-by-use-distinct': 'True',
      ...constructDynamicFilters(
        'document',
        filtering || [],
        undefined,
        selectedWorkflow?.id,
      ),
      self_and_descendants_of_owner_org_id__in: orgIds
        ? orgIds.join()
        : undefined,
      team_id__in: teamIds?.join(),
      any_flag__flag_level__in: flagCodes?.join(),
      no_flags: !flagCodes && noFlags ? 'true' : undefined,
      doc_project_tags__project_tag_id__in: tagIds?.join(),
    },
    refetchInterval: isApplyingFilters ? 2000 : false,
    enabled: isApplyingFilters,
  })

  const { idleCountByStateId, inFlightCountByStateId } = useMemo(() => {
    return groupedCounts.reduce<Record<string, Record<string, number>>>(
      (acc, val) => {
        if (val.document_workflow_states__processing_started_at__isnull) {
          acc.idleCountByStateId[val.workflow_states__id] = val.count
        } else {
          acc.inFlightCountByStateId[val.workflow_states__id] = val.count
        }
        return acc
      },
      { idleCountByStateId: {}, inFlightCountByStateId: {} },
    )
  }, [groupedCounts])

  const filteredIdleCountByStateId = useMemo(() => {
    return filteredGroupedCounts.reduce<Record<string, number>>((acc, val) => {
      if (val.document_workflow_states__processing_started_at__isnull) {
        acc[val.workflow_states__id] = val.count
      }
      return acc
    }, {})
  }, [filteredGroupedCounts])

  return useMemo(
    () => ({
      filteredIdleCountByStateId,
      idleCountByStateId,
      inFlightCountByStateId,
    }),
    [filteredIdleCountByStateId, idleCountByStateId, inFlightCountByStateId],
  )
}
